import { Button, Card, Container, Eyebrow, GoldDivider, TrophyMark } from '@tt/design-system';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CertificateCard } from '../../components/CertificateCard';
import { ShareLinks } from '../../components/ShareLinks';
import { SuccessSealing } from '../../components/SuccessSealing';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { APP_URL, BLOCKCHAIN_PROOF_URL } from '../../lib/env';
import { preloadPdf } from '../../lib/pdfPreloader';
import { useAuthStore } from '../../store/authStore';
import { useDraftStore } from '../../store/draftStore';
import { useRegistrationsStore } from '../../store/registrationsStore';

export default function MyCollection() {
  const user = useAuthStore((s) => s.user);
  const allRecords = useRegistrationsStore((s) => s.records);
  const records = useMemo(
    () => (user ? allRecords.filter((r) => r.ownerEmail === user.email) : []),
    [allRecords, user],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const justId = searchParams.get('just');
  const justRecord = useMemo(
    () => (justId ? records.find((r) => r.id === justId) : null),
    [justId, records],
  );
  const reduced = usePrefersReducedMotion();

  // Preload do PDF assim que entramos nessa rota — assim o "Baixar PDF"
  // do bloco cerimonial responde instantâneo.
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!justRecord) return;
    let cancelled = false;
    preloadPdf()
      .then(() => {
        if (!cancelled) setPdfReady(true);
      })
      .catch((err) => console.warn('Falha ao pré-carregar PDF', err));
    return () => {
      cancelled = true;
    };
  }, [justRecord]);

  // Quando chegamos via ?just=:id, o registro acabou de ser criado.
  // É seguro resetar o draft agora: já estamos numa rota pública/AppLayout
  // que não depende do DraftGate, então o reset não causa redirect indesejado.
  const resetDraft = useDraftStore((s) => s.reset);
  useEffect(() => {
    if (!justId) return;
    resetDraft();
  }, [justId, resetDraft]);

  async function handleDownloadPdf() {
    if (!justRecord) return;
    setPdfError(null);
    setPdfDownloading(true);
    try {
      const mod = await preloadPdf();
      await mod.downloadCertificatePdf(justRecord);
    } catch (err) {
      console.error(err);
      setPdfError('Não conseguimos gerar o PDF agora. Tente novamente em instantes.');
    } finally {
      setPdfDownloading(false);
    }
  }

  function clearJustParam() {
    const next = new URLSearchParams(searchParams);
    next.delete('just');
    setSearchParams(next, { replace: true });
  }

  if (!user) return null;

  const justPublicUrl = justRecord
    ? `${APP_URL.replace(/\/+$/, '')}/c/${justRecord.id}`
    : '';

  const fadeIn = reduced
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : {
        initial: { opacity: 0, y: 16 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: 1.6, duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
      };

  const cardAnim = reduced
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : {
        initial: { opacity: 0, y: 32 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: 1.0, duration: 0.8, ease: [0.16, 1, 0.3, 1] as const },
      };

  return (
    <Container size="wide" className="py-16 md:py-24">
      {justRecord ? (
        <section className="mb-16 flex flex-col items-center text-center" aria-live="polite">
          <SuccessSealing size={200} />

          <motion.div {...fadeIn} className="mt-8 flex flex-col items-center gap-3">
            <Eyebrow tone="gold">Você acaba de fazer parte da história</Eyebrow>
            <h1 className="font-display text-3xl leading-tight text-gold-shimmer md:text-5xl">
              Sua presença foi eternizada.
            </h1>
            <p className="max-w-xl text-sm leading-relaxed text-neutral-100 md:text-base">
              O selo é seu — único, permanente e verificável. Baixe seu certificado, compartilhe
              com quem importa ou explore as outras presenças abaixo.
            </p>
          </motion.div>

          <motion.div {...cardAnim} className="mt-10 w-full">
            <CertificateCard registration={justRecord} variant="public" size="md" />
          </motion.div>

          <motion.div {...fadeIn} className="mt-10 flex w-full flex-col items-center gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={handleDownloadPdf}
                disabled={pdfDownloading}
                aria-label="Baixar certificado em PDF"
              >
                {pdfDownloading
                  ? 'Gerando PDF…'
                  : pdfReady
                    ? 'Baixar certificado em PDF'
                    : 'Preparando certificado…'}
              </Button>

              <a
                href={BLOCKCHAIN_PROOF_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-gold/60 px-6 py-3 font-display text-base text-gold transition-colors duration-base hover:bg-gold/10"
              >
                Ver na blockchain
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M10 14L21 3" />
                  <path d="M21 9V3h-6" />
                  <path d="M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h5" />
                </svg>
              </a>

              <button
                type="button"
                onClick={clearJustParam}
                className="inline-flex items-center justify-center rounded-md border border-gold/30 px-6 py-3 font-display text-base text-champagne transition-colors duration-base hover:border-gold hover:text-gold"
              >
                Ver coleção
              </button>
            </div>

            {pdfError ? (
              <p role="alert" className="text-sm text-error">
                {pdfError}
              </p>
            ) : null}

            <GoldDivider variant="line" className="my-2 max-w-xs" />

            <div className="w-full max-w-md">
              <ShareLinks
                url={justPublicUrl}
                title="Estou na taça da Copa Feminina 2027!"
                text="Eternizei minha presença na Copa Feminina 2027. Veja meu selo:"
              />
            </div>

            <p className="font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
              Código de verificação · {justRecord.verificationCode}
            </p>
          </motion.div>
        </section>
      ) : null}

      <div className="mb-12 flex flex-col items-center text-center">
        <Eyebrow tone="gold" className="mb-3">
          Minha coleção
        </Eyebrow>
        <h2 className="font-display text-3xl text-parchment md:text-4xl">
          {justRecord ? 'Suas presenças eternizadas.' : 'As presenças que você eternizou.'}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-neutral-100 md:text-base">
          Cada selo guardado aqui é único, permanente e seu para sempre.
        </p>
      </div>

      {records.length === 0 ? (
        <div className="mx-auto flex max-w-md flex-col items-center gap-6 rounded-md border border-gold/20 bg-night/40 p-10 text-center">
          <TrophyMark variant="outline" size="lg" className="opacity-60" />
          <div>
            <p className="font-display text-2xl text-parchment">
              Sua taça ainda está esperando.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-neutral-100">
              Você ainda não eternizou nenhuma presença. Comece agora — é simples e leva poucos
              minutos.
            </p>
          </div>
          <Link
            to="/register"
            className="inline-flex items-center justify-center rounded-md bg-gold-gradient px-6 py-3 font-display text-base font-semibold text-night shadow-goldMedium transition-shadow duration-base hover:shadow-goldStrong"
          >
            Quero estar na taça
          </Link>
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {records.map((reg) => (
              <li key={reg.id}>
                <Link
                  to={`/c/${reg.id}`}
                  className="block rounded-md transition-transform duration-base hover:-translate-y-0.5"
                  aria-label={`Abrir certificado de ${reg.displayName}`}
                >
                  <Card
                    tone={reg.id === justId ? 'glow' : 'default'}
                    padding="sm"
                    className="overflow-hidden"
                  >
                    <CertificateCard registration={reg} variant="thumbnail" size="sm" />
                  </Card>
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-12 flex justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center rounded-md border border-gold/60 px-6 py-3 font-display text-base text-gold transition-colors duration-base hover:bg-gold/10"
            >
              Eternizar mais uma presença
            </Link>
          </div>
        </>
      )}
    </Container>
  );
}
